/* Buzz World — entry module (Phase 1: world core).
   Architecture: one shared `ctx`, modules register per-frame work with
   ctx.registerTick(fn). Debug/extension handle: window.bwInternals, announced
   with the `bw:internals-ready` event (Phase 2/3 modules plug in there). */
import * as THREE from 'three';
import { createCollisionWorld } from 'bw/collision';
import { buildEnvironment } from 'bw/environment';
import { buildTown } from 'bw/town';
import { createHumanoid, blobShadowTexture } from 'bw/character';
import { createInput } from 'bw/input';
import { buildCatalog, createProducts, loadExtraProducts } from 'bw/products';
import { planTown } from 'bw/stores';
import { createCrowd } from 'bw/npcs';
import { loadPeople, createPerson, loadStaticModel } from 'bw/people';
import { createAudio } from 'bw/audio';
import { loadTextures } from 'bw/textures';
import { loadDressing } from 'bw/dress';
import { createMenu } from 'bw/menu';
import { createCart } from 'bw/cart';
import { createVending } from 'bw/vending';
import { createMinimap } from 'bw/minimap';
import { createSearch } from 'bw/search';

const $ = (id) => document.getElementById(id);

function readJSON(id) {
  const el = $(id);
  if (!el) throw new Error('Missing #' + id);
  return JSON.parse(el.textContent || '{}');
}

/* Same device heuristic as store.js so both games agree on tiers. */
function detectQuality() {
  const n = navigator;
  const conn = n.connection || n.mozConnection || n.webkitConnection || null;
  const mem = Number(n.deviceMemory || 0), cores = Number(n.hardwareConcurrency || 0);
  const dpr = Number(window.devicePixelRatio || 1);
  const minSide = Math.min(window.innerWidth || 0, window.innerHeight || 0);
  let low = 0, high = 0;
  if (conn && conn.saveData) low += 3;
  if (mem && mem <= 2) low += 2; else if (mem >= 8) high += 1;
  if (cores && cores <= 4) low += 1; else if (cores >= 8) high += 1;
  if (dpr >= 3 && minSide <= 480) low += 1;
  if (minSide >= 1024 && dpr <= 2) high += 1;
  const forced = new URLSearchParams(location.search).get('bwq');
  const tier = forced || (low >= 2 ? 'low' : high >= 2 ? 'high' : 'standard');
  const table = {
    low: { tier: 'low', dprCap: 1.0, shadows: false, shadowMap: 512, antialias: false },
    standard: { tier: 'standard', dprCap: 1.5, shadows: true, shadowMap: 1024, antialias: true },
    high: { tier: 'high', dprCap: 2.0, shadows: true, shadowMap: 2048, antialias: true }
  };
  return table[tier] || table.standard;
}

function createLoader(i18n) {
  const bar = $('bwLoadBar'), pct = $('bwLoadPct'), overlay = $('bwLoading'), err = $('bwLoadError');
  let shown = 0;
  return {
    set(p) {
      shown = Math.max(shown, Math.min(1, p));
      if (bar) bar.style.transform = `scaleX(${shown})`;
      if (pct) pct.textContent = Math.round(shown * 100) + '%';
    },
    done() { if (overlay) { overlay.classList.add('is-done'); setTimeout(() => { overlay.hidden = true; }, 600); } },
    fail(message) {
      if (err) { err.hidden = false; err.querySelector('[data-bw-error-text]').textContent = message || i18n.loadingFailed; }
    }
  };
}

const nextFrame = () => new Promise((r) => requestAnimationFrame(() => r()));

async function boot() {
  const config = readJSON('bwConfig');
  const layout = readJSON('bwLayout');
  const i18n = config.i18n || {};
  const loader = createLoader(i18n);
  const root = $('bwRoot');
  const wrap = $('bwCanvasWrap');

  try {
    loader.set(0.05);
    const quality = detectQuality();
    root.dataset.quality = quality.tier;

    const renderer = new THREE.WebGLRenderer({ antialias: quality.antialias, powerPreference: 'high-performance', alpha: false });
    let dpr = Math.min(window.devicePixelRatio || 1, quality.dprCap);
    renderer.setPixelRatio(dpr);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    renderer.shadowMap.enabled = quality.shadows;
    renderer.shadowMap.type = THREE.PCFShadowMap;
    wrap.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, 1, 0.1, 900);

    const ticks = [];
    const ctx = {
      THREE, scene, camera, renderer, layout, config, quality, i18n,
      collisions: createCollisionWorld(layout),
      focus: null,
      registerTick(fn) { ticks.push(fn); return () => { const i = ticks.indexOf(fn); if (i >= 0) ticks.splice(i, 1); }; }
    };

    loader.set(0.15); await nextFrame();
    // Surface textures (~250 KB on phones, ~60 KB on low-end) and the palm model
    // from the original minigame (~80 KB), loaded together. Never block for long.
    const timeout = (ms) => new Promise((r) => setTimeout(() => r(null), ms));
    const [tex, palm, sam] = await Promise.all([
      Promise.race([loadTextures(config.textures, quality, renderer).catch(() => null), timeout(8000)]),
      config.palmUrl ? Promise.race([loadStaticModel(config.palmUrl, 5.6).catch((e) => { console.warn('[Buzz World] palm model failed', e); return null; }), timeout(8000)]) : null,
      config.models ? Promise.race([loadDressing(config.models).catch(() => null), timeout(10000)]) : null
    ]);
    ctx.textures = tex;
    ctx.palmModel = palm;
    ctx.sam = sam;
    ctx.environment = buildEnvironment(ctx);
    loader.set(0.4); await nextFrame();
    /* ---- catalog → town plan (Phase 5) ----
       One store per category, sized from the live catalog, dropped on the
       closest free plot. Nothing about the stores is hand-placed. */
    const extras = await loadExtraProducts(config);
    const catalog = buildCatalog(layout, extras);
    ctx.catalog = catalog;
    ctx.plan = planTown(layout, catalog.counts);
    loader.set(0.45); await nextFrame();

    ctx.town = buildTown(ctx);
    // The environment tells the town when the light changes (Phase 7).
    ctx.onDaylight = (state) => { if (ctx.town && ctx.town.setDaylight) ctx.town.setDaylight(state); };
    if (ctx.environment && ctx.environment.state) ctx.onDaylight(ctx.environment.state);
    loader.set(0.6); await nextFrame();

    ctx.products = createProducts(ctx, catalog);
    loader.set(0.7); await nextFrame();

    /* ---- entry: category links start in front of the right shelf ---- */
    const params = new URLSearchParams(location.search);
    const entryTags = String(params.get('tags') || '').split(/[,+]/).map((t) => t.trim()).filter(Boolean);
    const entry = ctx.products.entryPoint({ collection: params.get('collection') || '', tags: entryTags });
    // Coming back (e.g. from the cart page) puts you where you left off.
    let saved = null;
    try {
      const raw = JSON.parse(sessionStorage.getItem('bw-pos') || 'null');
      if (raw && Date.now() - raw.t < 30 * 60 * 1000 && isFinite(raw.x) && isFinite(raw.z)) saved = raw;
    } catch (_) {}
    const spawn = entry ? { x: entry.x, z: entry.z, yaw: entry.rotY } : (saved || layout.spawn || { x: 0, z: 10, yaw: 0 });

    /* ---- people models (fall back to the procedural rig if they fail) ---- */
    if (config.peopleUrl) {
      try { ctx.people = await loadPeople(config.peopleUrl); }
      catch (e) { console.warn('[Buzz World] people pack unavailable, using procedural characters', e); }
    }
    loader.set(0.72); await nextFrame();

    /* ---- player ---- */
    const look = Object.assign({ skin: '#d9a37e', hair: '#2b1b10', shirt: '#f2b61f', pants: '#16345e', shoes: '#ffffff', height: 1 }, layout.player || {});
    const player = ctx.people
      ? createPerson(ctx.people, {
          outfit: (layout.player && layout.player.outfit) || 'Casual',
          castShadow: quality.shadows, scale: look.height,
          palette: [look.skin, look.hair, '#15151a', look.shirt, '#ffffff', look.pants, look.shoes, '#16345e']
        })
      : createHumanoid(Object.assign({ castShadow: quality.shadows }, look));
    if (ctx.people && !quality.shadows) player.root.add(new THREE.Mesh(
      new THREE.PlaneGeometry(0.9, 0.9).rotateX(-Math.PI / 2),
      new THREE.MeshBasicMaterial({ map: blobShadowTexture(), transparent: true, depthWrite: false })
    ));
    const spawnPos = ctx.collisions.resolve({ x: spawn.x, z: spawn.z }, 0.34);
    player.root.position.set(spawnPos.x, ctx.collisions.groundHeight(spawnPos.x, spawnPos.z), spawnPos.z);
    player.root.rotation.y = Math.PI + (spawn.yaw || 0);
    scene.add(player.root);
    ctx.player = player;
    ctx.focus = player.root.position;

    /* ---- camera rig ---- */
    const cam = {
      yaw: spawn.yaw || 0, pitch: 0.32, dist: 6.2, distSmoothed: 6.2,
      minDist: 3, maxDist: 11, minPitch: 0.08, maxPitch: 1.05,
      target: new THREE.Vector3(), desired: new THREE.Vector3()
    };
    // Gold underline beneath the product in reach.
    const highlight = new THREE.Mesh(
      new THREE.BoxGeometry(1, 0.022, 0.022),
      new THREE.MeshBasicMaterial({ color: '#ffd54a', transparent: true, opacity: 0.95, depthWrite: false, toneMapped: false })
    );
    highlight.visible = false;
    highlight.renderOrder = 3;
    scene.add(highlight);

    /* ---- product popup + cart ---- */
    let popupOpen = false;
    function openProduct(p) {
      if (!p) return;
      const seed = ctx.products.seedFor(p);
      if (window.BuzzGlobalPopup && window.BuzzGlobalPopup.open) window.BuzzGlobalPopup.open(p.handle, seed);
      else if (window.__bzpQueue) window.__bzpQueue.push({ handle: p.handle, seed });
      else location.href = '/products/' + encodeURIComponent(p.handle);
    }
    const cartBtn = $('bwCart'), cartCount = $('bwCartCount');
    let lastCount = null;
    function refreshCart() {
      fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((c) => {
          if (!c || !cartCount) return;
          const n = Math.max(0, c.item_count || 0);
          cartCount.textContent = String(n);
          cartCount.hidden = n === 0;
          if (lastCount !== null && n > lastCount) { cartCount.classList.remove('is-bump'); void cartCount.offsetWidth; cartCount.classList.add('is-bump'); if (ctx.ui && ctx.ui.sfx) ctx.ui.sfx('coin'); }
          lastCount = n;
        })
        .catch(() => {});
    }
    refreshCart();
    document.addEventListener('cart:refresh', () => setTimeout(refreshCart, 150));
    document.addEventListener('visibilitychange', () => { if (!document.hidden) refreshCart(); });
    if (cartBtn) cartBtn.addEventListener('click', () => { location.href = config.cartUrl || '/cart'; });

    const input = createInput(wrap, $('bwJoystick'), {
      onTap(x, y) {
        if (popupOpen) return;
        const hit = ctx.products.pick(x, y, camera, wrap.getBoundingClientRect(), 14);
        if (hit) {
          const s = hit.slot, pp = player.root.position;
          if (Math.hypot(s.x - pp.x, s.z - pp.z) < 9) {
            const st = ctx.town.stores.find((x2) => x2.id === s.store) || ctx.town.storeAt(s.x, s.z);
            if (st && ctx.ui.openVending) ctx.ui.openVending(st); else openProduct(hit);
          }
        }
      }
    });
    const prompt = $('bwPrompt');
    const promptTitle = prompt && prompt.querySelector('[data-bw-prompt-title]');
    const promptPrice = prompt && prompt.querySelector('[data-bw-prompt-price]');
    const hint = $('bwHint');

    function resize() {
      const w = Math.max(1, wrap.clientWidth), h = Math.max(1, wrap.clientHeight);
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.fov = w < h ? 68 : 55; // portrait phones need a wider view
      camera.updateProjectionMatrix();
    }
    resize();
    window.addEventListener('resize', resize, { passive: true });
    if (window.visualViewport) window.visualViewport.addEventListener('resize', resize, { passive: true });

    /* ---- per-frame player + camera ---- */
    const vel = new THREE.Vector2();
    const WALK = 2.8, RUN = 5.6;
    let insideShop = false;
    let currentStore = null;
    let playerSpeed = 0;
    let focusBlend = 0;
    let hintHidden = false;

    let lookHold = 0; // seconds since the camera was last dragged
    function updatePlayer(dt) {
      const inp = input.poll();
      cam.yaw -= inp.lookDX * 0.006;
      lookHold = Math.abs(inp.lookDX) > 0.3 ? 0 : lookHold + dt;
      cam.pitch = THREE.MathUtils.clamp(cam.pitch + inp.lookDY * 0.004, cam.minPitch, cam.maxPitch);
      cam.dist = THREE.MathUtils.clamp(cam.dist + inp.zoom, cam.minDist, cam.maxDist);

      const fx = -Math.sin(cam.yaw), fz = -Math.cos(cam.yaw);
      const rx = Math.cos(cam.yaw), rz = -Math.sin(cam.yaw);
      let mag = Math.min(1, Math.hypot(inp.moveX, inp.moveZ));
      let speed = mag * (inp.run ? RUN : WALK);
      let dx = fx * -inp.moveZ + rx * inp.moveX;
      let dz = fz * -inp.moveZ + rz * inp.moveX;
      const dl = Math.hypot(dx, dz) || 1;
      dx /= dl; dz /= dl;
      // Auto-walk to a searched product (any manual input takes over).
      if (ctx.search) {
        if (mag > 0.1) ctx.search.cancelAutopilot();
        const auto = ctx.search.steer();
        if (auto) {
          dx = auto.x; dz = auto.z; mag = 1;
          speed = auto.run ? RUN * 0.85 : WALK * 1.1;
          if (Math.abs(inp.lookDX) < 0.5) {
            let dy = Math.atan2(-dx, -dz) - cam.yaw;
            dy = Math.atan2(Math.sin(dy), Math.cos(dy));
            cam.yaw += dy * Math.min(1, dt * 1.6);
          }
        }
      }

      const accel = mag > 0 ? 10 : 12;
      vel.x += (dx * speed - vel.x) * Math.min(1, dt * accel);
      vel.y += (dz * speed - vel.y) * Math.min(1, dt * accel);
      if (mag === 0 && vel.lengthSq() < 0.0004) vel.set(0, 0);

      const p = player.root.position;
      const from = { x: p.x, z: p.z };
      const moved = ctx.collisions.move(from, { x: p.x + vel.x * dt, z: p.z + vel.y * dt }, 0.34);
      if (ctx.npcs) {
        ctx.npcs.pushPlayer(moved);
        ctx.collisions.resolve(moved, 0.34);
        if (!ctx.collisions.isWalkable(moved.x, moved.z)) { moved.x = from.x; moved.z = from.z; }
      }
      const realSpeed = Math.hypot(moved.x - from.x, moved.z - from.z) / Math.max(dt, 1e-4);
      p.x = moved.x; p.z = moved.z;
      const gy = ctx.collisions.groundHeight(p.x, p.z);
      p.y += (gy - p.y) * Math.min(1, dt * 14);

      /* Third-person camera: the camera stays behind the character.
         - Walking: the character faces where it walks and the camera swings in
           behind it (gently, so it never feels jumpy).
         - Standing and dragging the camera: the character turns on the spot to
           face the same way as the camera. */
      const behind = (yaw) => { const a = yaw + Math.PI; return Math.atan2(Math.sin(a), Math.cos(a)); };
      const turnTo = (target, rate) => {
        let diff = target - player.root.rotation.y;
        diff = Math.atan2(Math.sin(diff), Math.cos(diff));
        player.root.rotation.y += diff * Math.min(1, dt * rate);
      };
      if (mag > 0.05) {
        turnTo(Math.atan2(dx, dz), 12);
        if (lookHold > 0.35 && !(ctx.search && ctx.search.isAutopilot())) {
          let dy = (player.root.rotation.y - Math.PI) - cam.yaw;
          dy = Math.atan2(Math.sin(dy), Math.cos(dy));
          cam.yaw += dy * Math.min(1, dt * 2.2);
        }
        if (!hintHidden && hint) { hintHidden = true; hint.classList.add('is-hidden'); }
      } else if (lookHold < 0.25) {
        turnTo(behind(cam.yaw), 14);
      }
      player.update(dt, realSpeed);
      playerSpeed = realSpeed;

      // Store interior: hide roof, tighten camera.
      const nowStore = ctx.town.storeAt(p.x, p.z);
      if (nowStore !== currentStore) {
        if (currentStore) { currentStore.setCutaway(false); if (currentStore.setOccupied) currentStore.setOccupied(false); }
        currentStore = nowStore;
        insideShop = !!nowStore;
        // Inside a store you can't see the other buildings: don't draw them.
        ctx.town.stores.forEach((st) => { if (st.group) st.group.visible = !nowStore || st === nowStore || st.kind === 'stall'; });
        if (currentStore) { currentStore.setCutaway(true); if (currentStore.setOccupied) currentStore.setOccupied(true); }
        root.classList.toggle('is-in-shop', insideShop);
      }
    }

    function updateCamera(dt) {
      const p = player.root.position;
      // In the store: high view while walking the aisles, close-up on the shelf when browsing.
      const browsing = insideShop && nearProduct && playerSpeed < 0.3;
      focusBlend += ((browsing ? 1 : 0) - focusBlend) * Math.min(1, dt * 3);
      const shopDist = THREE.MathUtils.clamp(cam.dist, 5, 8);
      const maxD = insideShop ? THREE.MathUtils.lerp(shopDist, 4.2, focusBlend) : cam.dist;
      const pitch = insideShop ? THREE.MathUtils.lerp(Math.max(cam.pitch, 0.9), 0.5, focusBlend) : cam.pitch;
      cam.target.set(p.x, p.y + 1.45, p.z);
      if (focusBlend > 0.001 && nearProduct) {
        // Frame the shelf, not the back of the player's head.
        const s = nearProduct.slot;
        cam.target.x += (s.x - p.x) * 0.6 * focusBlend;
        cam.target.z += (s.z - p.z) * 0.6 * focusBlend;
        cam.target.y += (s.y + 0.15 - cam.target.y) * 0.6 * focusBlend;
        cam.target.x += Math.cos(cam.yaw) * 0.55 * focusBlend;
        cam.target.z += -Math.sin(cam.yaw) * 0.55 * focusBlend;
      }
      const cp = Math.cos(pitch), sp = Math.sin(pitch);
      const ox = Math.sin(cam.yaw) * cp, oz = Math.cos(cam.yaw) * cp;
      let hit = 1;
      if (!insideShop) hit = ctx.collisions.segmentHit(cam.target.x, cam.target.z, cam.target.x + ox * maxD, cam.target.z + oz * maxD, 2.4, 0.3);
      const want = Math.max(1.4, maxD * hit - 0.2);
      cam.distSmoothed += (want - cam.distSmoothed) * Math.min(1, dt * (want < cam.distSmoothed ? 18 : 3));
      const d = cam.distSmoothed;
      cam.desired.set(cam.target.x + ox * d, cam.target.y + sp * d, cam.target.z + oz * d);
      const floorY = ctx.collisions.groundHeight(cam.desired.x, cam.desired.z) + 0.5;
      if (cam.desired.y < floorY) cam.desired.y = floorY;
      camera.position.lerp(cam.desired, Math.min(1, dt * 12));
      camera.lookAt(cam.target);
    }

    /* Product in reach → gold underline + "View" prompt. */
    let nearProduct = null;
    /* ---- Vending popup: the store's own vending machine, near full screen ----
       Shelves in the world are decoration; "Δες <κατηγορία>" opens the real
       vending view (search template, ?view=bwframe) for that category. It is
       preloaded in the background so it opens without waiting. */
    const vending = $('bwVending');
    const vFrame = vending && vending.querySelector('iframe');
    const vTitle = vending && vending.querySelector('[data-bw-vending-title]');
    const vClose = vending && vending.querySelector('[data-bw-vending-close]');
    let vendingOpen = false;
    const vendingView = createVending(ctx, vFrame);
    function openVending(store) {
      if (!vending || !vFrame || !store) return;
      vendingView.load(store);
      if (vTitle) vTitle.textContent = (store.emoji ? store.emoji + ' ' : '') + (store.label || '');
      vending.hidden = false;
      requestAnimationFrame(() => vending.classList.add('is-open'));
      vendingOpen = true;
      try { history.pushState({ bwVending: 1 }, ''); } catch (_) {}
      if (ctx.ui && ctx.ui.sfx) ctx.ui.sfx('open');
    }
    function closeVending(fromHistory) {
      if (!vendingOpen) return;
      vendingOpen = false;
      vendingView.cancel();
      vending.classList.remove('is-open');
      setTimeout(() => { if (!vendingOpen) vending.hidden = true; }, 220);
      refreshCart();
      if (!fromHistory && history.state && history.state.bwVending) { try { history.back(); } catch (_) {} }
    }
    if (vClose) vClose.addEventListener('click', () => closeVending(false));
    window.addEventListener('keydown', (e) => { if (e.key === 'Escape' && vendingOpen && !document.querySelector('.bzp-overlay.is-open')) closeVending(false); });
    window.addEventListener('popstate', () => { if (vendingOpen) closeVending(true); });
    ctx.ui = Object.assign(ctx.ui || {}, { openVending, isVendingOpen: () => vendingOpen });

    /* Near or inside a store/stall → one clear button: "Δες <κατηγορία>". */
    let promptStore = null;
    function updateSlots() {
      const p = player.root.position;
      let best = currentStore || null;
      if (!best && !popupOpen) {
        let bd = 5.5;
        ctx.town.stores.forEach((st) => {
          const e = st.entrance;
          const d = e ? Math.hypot(e[0] - p.x, e[1] - p.z) : Infinity;
          if (d < bd) { bd = d; best = st; }
        });
      }
      if (popupOpen) best = null;
      if (best === promptStore) return;
      promptStore = best;
      nearProduct = null;
      highlight.visible = false;
      root.classList.toggle('has-prompt', !!best);
      if (best && ctx.ui && ctx.ui.sfx) ctx.ui.sfx('tick');
      if (prompt) {
        prompt.classList.toggle('is-visible', !!best);
        if (best) {
          if (promptTitle) promptTitle.textContent = (best.emoji ? best.emoji + ' ' : '') + (best.label || '');
          if (promptPrice) promptPrice.textContent = '';
        }
      }
    }
    if (prompt) prompt.addEventListener('click', () => openVending(promptStore));

    /* ---- toast (short guidance messages) ---- */
    const toastEl = $('bwToast');
    let toastTimer = 0;
    function toast(text, ms) {
      if (!toastEl || !text) return;
      toastEl.textContent = text;
      toastEl.classList.add('is-visible');
      clearTimeout(toastTimer);
      toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), ms || 2600);
    }
    ctx.ui = Object.assign(ctx.ui || {}, { openProduct, toast, isPopupOpen: () => popupOpen, getNearProduct: () => nearProduct });

    /* ---- people (Phase 3) ---- */
    loader.set(0.74); await nextFrame();
    ctx.npcs = createCrowd(ctx);
    ctx.cam = cam;

    /* ---- Phase 4: audio, minimap, search ---- */
    const audio = createAudio();
    ctx.audio = audio;
    ctx.ui.sfx = (name) => audio.sfx(name);
    const unlock = () => audio.start();
    root.addEventListener('pointerdown', unlock, { passive: true });
    window.addEventListener('keydown', unlock);
    const soundBtn = $('bwSound');
    const syncSound = () => {
      if (!soundBtn) return;
      soundBtn.classList.toggle('is-muted', audio.isMuted());
      soundBtn.setAttribute('aria-pressed', audio.isMuted() ? 'true' : 'false');
    };
    syncSound();
    if (soundBtn) soundBtn.addEventListener('click', () => { audio.start(); audio.setMuted(!audio.isMuted()); syncSound(); });

    // The minimap was removed from the world UI (categories live at the top now).
    ctx.minimap = config.minimap === true ? createMinimap(ctx, $('bwMap')) : null;
    ctx.search = createSearch(ctx, {
      panel: $('bwSearch'), input: $('bwSearchInput'), results: $('bwSearchResults'), chips: $('bwSearchChips'),
      closeBtn: $('bwSearchClose'), compass: $('bwCompass')
    });
    ctx.cart = createCart(ctx, $('bwCartPanel'));
    ctx.menu = createMenu(ctx, { bar: $('bwBar'), sheet: $('bwSheet') });
    const searchBtn = $('bwSearchBtn');
    if (searchBtn) searchBtn.addEventListener('click', () => ctx.search.openPanel());

    let nearStore = ctx.town.nearestStore(player.root.position.x, player.root.position.z);
    const pierZone = (layout.piers || [])[0];
    function audioEnv() {
      const p = player.root.position;
      const r = Math.hypot(p.x, p.z);
      const onPier = pierZone && Math.abs(p.x - pierZone.x) < pierZone.w / 2 + 0.2 && Math.abs(p.z - pierZone.z) < pierZone.len / 2 + 1;
      return {
        distToShore: Math.max(0, layout.world.shoreRadius - 2 - r) * (onPier ? 0 : 1),
        insideShop, distToShop: nearStore.distance,
        speed: playerSpeed,
        surface: insideShop || onPier ? 'wood' : r > layout.world.flatRadius - 6 ? 'sand' : 'stone'
      };
    }

    /* Performance: hide shelf products when far from the store. */
    let cullT = 0;
    function cullProducts(dt) {
      cullT += dt;
      if (cullT < 0.5) return;
      cullT = 0;
      nearStore = ctx.town.nearestStore(camera.position.x, camera.position.z);
      // Only the stores you can actually see keep their shelves rendered.
      ctx.town.stores.forEach((st) => {
        const g = ctx.products.storeGroups.get(st.id);
        if (!g) return;
        const d = Math.hypot(camera.position.x - st.spec.x, camera.position.z - st.spec.z);
        g.visible = (currentStore === st) || d < 34;
      });
    }

    /* Debug overlay (?bwdebug=1): FPS, draw calls, triangles, resolution, crowd. */
    const dbg = params.has('bwdebug') ? $('bwDebug') : null;
    if (dbg) dbg.hidden = false;
    const dbgState = { t: 0, frames: 0 };
    function debugOverlay(dt) {
      if (!dbg) return;
      dbgState.t += dt; dbgState.frames++;
      if (dbgState.t < 0.5) return;
      const info = renderer.info.render;
      const st = ctx.npcs.stats();
      dbg.textContent = `${Math.round(dbgState.frames / dbgState.t)} fps · ${info.calls} calls · ${Math.round(info.triangles / 1000)}k tris · dpr ${dpr.toFixed(2)} · ${quality.tier} · npc ${st.active}/${st.total}`;
      dbgState.t = 0; dbgState.frames = 0;
    }

    /* Pause movement while the global product popup is open. */
    let popupCheck = 0;
    function checkPopup(dt) {
      popupCheck += dt;
      if (popupCheck < 0.2) return;
      popupCheck = 0;
      const open = !!document.querySelector('.bzp-overlay.is-open, .bzp-image-preview.is-open, .bzch--world-help.bzch--open') || !!(ctx.search && ctx.search.isOpen()) || !!(ctx.ui && ctx.ui.isVendingOpen && ctx.ui.isVendingOpen()) || !!(ctx.cart && ctx.cart.isOpen());
      if (open !== popupOpen) {
        popupOpen = open;
        input.setActive(!open);
        root.classList.toggle('is-popup-open', open);
      }
    }

    /* Adaptive resolution: keeps phones smooth when the scene gets busy. */
    const perf = { acc: 0, frames: 0, window: 2.5, minDpr: 0.75 };
    function adapt(dt) {
      perf.acc += dt; perf.frames++;
      if (perf.acc < perf.window) return;
      const avg = perf.acc / perf.frames;
      perf.acc = 0; perf.frames = 0;
      const cap = Math.min(window.devicePixelRatio || 1, quality.dprCap);
      let next = dpr;
      if (avg > 1 / 40 && dpr <= perf.minDpr && ctx.npcs) ctx.npcs.reduce();
      if (avg > 1 / 40) next = Math.max(perf.minDpr, dpr - 0.25);
      else if (avg < 1 / 57 && dpr < cap) next = Math.min(cap, dpr + 0.25);
      if (next !== dpr) { dpr = next; renderer.setPixelRatio(dpr); resize(); }
      root.dataset.dpr = dpr.toFixed(2);
    }

    loader.set(0.8); await nextFrame();
    currentStore = ctx.town.storeAt(player.root.position.x, player.root.position.z);
    if (currentStore) {
      insideShop = true;
      currentStore.setCutaway(true, true);
      if (currentStore.setOccupied) currentStore.setOccupied(true);
      root.classList.add('is-in-shop');
      cam.distSmoothed = 6;
    }
    updateCamera(1);
    camera.position.copy(cam.desired);
    camera.lookAt(cam.target);
    // Shader warm-up: compile every material before the first visible frame.
    if (renderer.compileAsync) await renderer.compileAsync(scene, camera);
    else renderer.compile(scene, camera);
    renderer.render(scene, camera);
    loader.set(1);

    /* ---- loop ---- */
    let running = true;
    let elapsed = 0;
    let last = performance.now();
    function frame(now) {
      if (!running) return;
      now = now || performance.now();
      const dt = Math.min(Math.max((now - last) / 1000, 0), 0.05);
      last = now;
      checkPopup(dt);
      audio.update(dt, audioEnv());
      // Performance: freeze the world (no simulation, no rendering) while the
      // product popup or the search panel covers it. The last frame stays on screen.
      if (popupOpen) { requestAnimationFrame(frame); return; }
      elapsed += dt;
      updatePlayer(dt);
      for (let i = 0; i < ticks.length; i++) ticks[i](dt, elapsed);
      if (ctx.npcs) ctx.npcs.update(dt, elapsed, camera);
      updateCamera(dt);
      if (ctx.npcs) ctx.npcs.afterCamera(camera);
      updateSlots();
      ctx.search.update(dt, cam.yaw);
      if (ctx.minimap) ctx.minimap.update(dt, elapsed);
      cullProducts(dt);
      if (highlight.visible) highlight.material.opacity = 0.65 + Math.sin(elapsed * 5) * 0.3;
      renderer.render(scene, camera);
      adapt(dt);
      debugOverlay(dt);
      requestAnimationFrame(frame);
    }
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) { running = false; input.setActive(false); audio.suspend(); }
      else if (!running) { running = true; input.setActive(true); audio.resume(); last = performance.now(); requestAnimationFrame(frame); }
    });
    requestAnimationFrame(frame);
    loader.done();
    root.classList.add('is-ready');
    const savePos = () => {
      try {
        const q = player.root.position;
        sessionStorage.setItem('bw-pos', JSON.stringify({ x: +q.x.toFixed(2), z: +q.z.toFixed(2), yaw: +(player.root.rotation.y - Math.PI).toFixed(3), t: Date.now() }));
      } catch (_) {}
    };
    setInterval(savePos, 2000);
    window.addEventListener('pagehide', savePos);
    ctx.products.loadImages(() => player.root.position);
    // Warm the vending view (theme CSS/JS, fonts) so the first "Δες" is instant.


    const report = ctx.products.report();
    if (params.has('bwdebug')) console.log('[Buzz World] products', report, 'crowd', ctx.npcs.stats());

    window.bwInternals = { ctx, player, cam, input, openProduct, refreshCart, report, toast, get nearProduct() { return nearProduct; } };
    window.dispatchEvent(new CustomEvent('bw:internals-ready', { detail: window.bwInternals }));
  } catch (e) {
    console.error('[Buzz World]', e);
    loader.fail((i18n.loadingFailed || 'Loading failed') + (config.debug ? ': ' + e.message : ''));
  }
}

/* Exit + retry buttons work even if the 3D boot fails. */
function wireChrome() {
  // Links marked data-bw-mode="2d" (classic view) remember the choice.
  document.addEventListener('click', (e) => {
    const a = e.target.closest && e.target.closest('[data-bw-mode]');
    if (a) { try { localStorage.setItem('bw-mode', a.getAttribute('data-bw-mode')); } catch (_) {} }
  }, true);
  let cfg = {};
  try { cfg = readJSON('bwConfig'); } catch (_) {}
  const exitBtn = $('bwExit');
  if (exitBtn) exitBtn.addEventListener('click', () => {
    let sameOrigin = false;
    try { sameOrigin = !!document.referrer && new URL(document.referrer).origin === location.origin; } catch (_) {}
    if (sameOrigin && history.length > 1) history.back();
    else location.href = cfg.exitUrl || '/';
  });
  const retry = $('bwRetry');
  if (retry) retry.addEventListener('click', () => location.reload());
}

wireChrome();
boot();
